import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PrescriptionDto, PrescriptionStatus, ValidatePrescriptionDto } from '@services/dtos'
import { ModalityEnum, PaginatedQuery } from '@services/api'
import { PaginatedDto } from '@services/extendedType'
import { PrescriptionService } from '@services/prescriptionService'
import moment from 'moment'

export interface PrescriptionFilters extends PaginatedQuery {
  status?: PrescriptionStatus
  from?: string
  to?: string
  examIds?: number[]
  modality?: ModalityEnum
  patientId?: number
  cancelledAppointment?: boolean
}

export interface PrescriptionState {
  prescriptions: PaginatedDto<PrescriptionDto>
  filters: PrescriptionFilters
  loading: boolean
}

const initialState: PrescriptionState = {
  prescriptions: {
    datas: [],
    totalCount: 0,
  },
  filters: {
    from: moment().format('YYYY-MM-DD'),
    to: moment().add(5, 'days').format('YYYY-MM-DD'),
    limit: 20,
    page: 0,
  },
  loading: false,
}

export const getPrescriptions = createAsyncThunk<
  PaginatedDto<PrescriptionDto>,
  void
>('prescriptions/find', async (_, { getState }) => {
  const { filters } = (getState() as { prescription: PrescriptionState })
    .prescription
  return PrescriptionService.getAll(filters)
})

export const validatePrescription = createAsyncThunk<PrescriptionDto, { id: string; dto: ValidatePrescriptionDto }>(
  'prescriptions/validate',
  async ({ id, dto }) => PrescriptionService.validate(id, dto),
)

export const invalidatePrescription = createAsyncThunk<PrescriptionDto, { id: string }>(
  'prescriptions/invalidate',
  async ({ id }) => PrescriptionService.invalidate(id),
)

export const prescriptionReducer = createSlice({
  name: 'prescriptions',
  initialState,
  reducers: {
    setPrescriptionFilters: (
      state,
      action: PayloadAction<PrescriptionFilters>,
    ) => {
      state.filters = {
        ...state.filters,
        page: 0,
        ...action.payload,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPrescriptions.pending, (state) => {
        state.loading = true
      })
      .addCase(getPrescriptions.rejected, (state) => {
        state.loading = false
      })
      .addCase(getPrescriptions.fulfilled, (state, action) => {
        state.prescriptions = action.payload
        state.loading = false
      })
      .addCase(validatePrescription.fulfilled, (state, action) => {
        state.prescriptions.datas = state.prescriptions.datas.map(
          (prescription) =>
            prescription.id === action.payload.id
              ? action.payload
              : prescription,
        )
      })
      .addCase(invalidatePrescription.fulfilled, (state, action) => {
        state.prescriptions.datas = state.prescriptions.datas.map(
          (prescription) =>
            prescription.id === action.payload.id
              ? action.payload
              : prescription,
        )
      })
  }
})

export default prescriptionReducer.reducer

export const { setPrescriptionFilters } = prescriptionReducer.actions
